import { createContext, useContext, useEffect, useMemo, useRef } from 'react';
import { Link, useNavigate, type LinkProps } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import {
  ASSISTANT_ITEM_LINK_TYPES, type AssistantLinkRef, type AssistantObjectRef, type AssistantResolvedObject,
} from '@contracts/assistant';
import { focusHref, focusWorkplaceItem } from '../workplace/focus-request';
import { useNarrowView } from '../../lib/media';
import { cn } from '../../lib/utils';
import { useAssistantOptional } from './AssistantProvider';
import { ASSISTANT_KEY, resolveObjects } from './api';
import { hoverObject } from './glow';

/** The resolve route takes at most this many refs (spec §6.2); a longer message asks in batches (P2 review F14). */
const RESOLVE_LIMIT = 50;

export const refKey = (r: AssistantLinkRef): string => `${r.type}:${r.id}`;

function unique(refs: readonly AssistantLinkRef[]): AssistantLinkRef[] {
  const seen = new Map<string, AssistantLinkRef>();
  for (const r of refs) if (!seen.has(refKey(r))) seen.set(refKey(r), { type: r.type, id: r.id });
  return [...seen.values()];
}

export const objectsKey = (refs: readonly AssistantLinkRef[]) =>
  [...ASSISTANT_KEY, 'objects', ...unique(refs).map(refKey).sort()] as const;

/**
 * Chips already named by the page's own answer (the MCP confirm page, S2 D75): `useResolved` reads them from here and
 * asks Hana's resolve route nothing, so the page works with Hana switched off. A ref the answer leaves out is one this
 * person cannot see.
 */
export const ResolvedObjectsContext = createContext<readonly AssistantResolvedObject[] | null>(null);

/** What a chip shows: the server's answer, 'failed' when the resolve itself failed, undefined while it is on its way. */
export type ChipState = AssistantResolvedObject | 'failed' | undefined;

/**
 * One resolve per message (a batch per 50 refs): the server names every object and says where it lives, or that this
 * person can no longer see it. Re-checked each time the message renders anew (spec §6.2): a name the person lost access
 * to must not linger in a cache. Once the answer is in, every chip has an end state: a ref the answer leaves out is one
 * this person cannot see, and a failed resolve says so (P2 review F14).
 */
export function useResolved(refs: readonly AssistantLinkRef[]): ReadonlyMap<string, ChipState> {
  const given = useContext(ResolvedObjectsContext);
  const list = unique(refs);
  const key = objectsKey(list);
  const asked = useQuery({
    queryKey: key,
    queryFn: async () => {
      const batches: AssistantLinkRef[][] = [];
      for (let i = 0; i < list.length; i += RESOLVE_LIMIT) batches.push(list.slice(i, i + RESOLVE_LIMIT));
      return (await Promise.all(batches.map((b) => resolveObjects(b)))).flat();
    },
    enabled: list.length > 0 && given === null,
    staleTime: 0,
    refetchOnMount: 'always',
  });
  const data = given ?? asked.data;
  const isError = given === null && asked.isError;
  const askedKey = key.join('|');
  return useMemo(() => {
    const byKey = new Map<string, ChipState>();
    if (data !== undefined) {
      for (const r of list) byKey.set(refKey(r), { ...r, label: null, href: null });
      for (const o of data) byKey.set(refKey(o), o);
    } else if (isError) {
      for (const r of list) byKey.set(refKey(r), 'failed');
    }
    return byKey;
    // `askedKey` stands for `list`, which is rebuilt on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, isError, askedKey]);
}

/** The mockup's `.chip`: a small rounded pill in the primary chip colours. Inline, not inline-block: a long name wraps
 *  with the sentence instead of dropping onto its own lines (each line piece keeps its padding and corners). */
const CHIP = 'inline rounded-[4px] px-1 [box-decoration-break:clone] [-webkit-box-decoration-break:clone]';
const LIVE = `${CHIP} bg-[hsl(var(--h-tone-primary-chip-bg))] text-[hsl(var(--h-tone-primary-chip-fg))]`;

/**
 * An object Hana named, as the server names it (D77): a link to it, plain text for a person, or a grey 'Không còn truy
 * cập'. The model's own label is never shown, not even while the answer is on its way. Hovering or focusing the chip
 * outlines the object's card on the page; on a phone a click first closes the sheet that covers the page.
 */
/** The card a chip outlines: a `work` link outlines the item as the server typed it, once the answer is in. */
function cardOf(objectRef: AssistantLinkRef, resolved: ChipState): AssistantObjectRef | null {
  if (objectRef.type !== 'work') return { type: objectRef.type, id: objectRef.id };
  const itemType = resolved === undefined || resolved === 'failed' ? null : resolved.itemType ?? null;
  return itemType === null ? null : { type: itemType, id: objectRef.id };
}

export function ObjectChip({ objectRef, resolved, onNavigate }: {
  objectRef: AssistantLinkRef; resolved: ChipState; onNavigate?: () => void;
}) {
  // Null on the MCP confirm page with Hana off: there is no panel to step aside.
  const assistant = useAssistantOptional();
  const narrow = useNarrowView();
  const navigate = useNavigate();
  const hovered = useRef<AssistantObjectRef | null>(null);
  const card = cardOf(objectRef, resolved);
  const hover = (on: boolean) => {
    if (card === null) return;
    hovered.current = on ? card : null;
    hoverObject(card, on);
  };
  // A chip that leaves the screen while hovered takes its outline with it.
  const { type, id } = objectRef;
  useEffect(() => () => { if (hovered.current !== null) hoverObject(hovered.current, false); }, [type, id]);
  const handlers = {
    onPointerEnter: () => hover(true), onPointerLeave: () => hover(false),
    onFocus: () => hover(true), onBlur: () => hover(false),
  };

  if (resolved === undefined) {
    return <span data-chip={refKey(objectRef)} aria-busy="true"
      className={cn(LIVE, 'inline-block h-[1.1em] w-14 animate-pulse align-middle motion-reduce:animate-none')} />;
  }
  if (resolved === 'failed') {
    return (
      <span data-chip={refKey(objectRef)} aria-busy="false"
        className={cn(CHIP, 'bg-[hsl(var(--h-muted))] text-[hsl(var(--h-muted-foreground))]')}>
        Không tải được
      </span>
    );
  }
  if (resolved.label === null) {
    return (
      <span data-chip={refKey(objectRef)}
        className={cn(CHIP, 'bg-[hsl(var(--h-muted))] text-[hsl(var(--h-muted-foreground))]')}>
        Không còn truy cập
      </span>
    );
  }
  if (resolved.href === null) {
    return <span data-chip={refKey(objectRef)} className={LIVE} {...handlers}>{resolved.label}</span>;
  }
  // A Workplace item (task, note, work) is focused on the board, not opened (T2): still a real link, so a new tab,
  // a middle click or a copied address lands on the Workplace with the card focused; a plain click stays in the app.
  const item = (ASSISTANT_ITEM_LINK_TYPES as readonly string[]).includes(objectRef.type);
  const onClick: LinkProps['onClick'] = (event) => {
    const plain = event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey;
    if (item && !plain) return;
    hover(false);
    if (narrow) assistant?.setOpen(false);
    onNavigate?.();
    if (!item) return;
    event.preventDefault();
    focusWorkplaceItem(objectRef.id, { moveFocus: narrow }, (to, opts) => { void navigate(to, opts); });
  };
  return (
    <Link data-chip={refKey(objectRef)} to={item ? focusHref(objectRef.id) : resolved.href} {...handlers}
      className={cn(LIVE, 'underline-offset-2 hover:underline')}
      onClick={onClick}>
      {resolved.label}
    </Link>
  );
}
